import { useEffect } from 'react';
import { useFedStore } from '../store';
import { Ic } from '../icons';
import { Rv, Eyebrow } from '../ui';

const UPDATED = '5 августа 2026 года';
const OPERATOR = 'ОО «Ecotime» в рамках проекта «Федерация киберспорта СКО»';

function LegalLayout({ type, title, intro, sections }) {
  const { go } = useFedStore();
  useEffect(() => {
    document.title = `${title} — Федерация киберспорта СКО`;
    return () => { document.title = 'Федерация киберспорта СКО'; };
  }, [title]);

  return (
    <div className="fed-legal-page fed-pagewrap max-w-[1060px] mx-auto px-6">
      <Rv className="fed-legal-head">
        <Eyebrow>{type}</Eyebrow>
        <h1>{title}</h1>
        <p>{intro}</p>
        <div className="fed-legal-meta">
          <span><Ic name="calendar" size={15} />Редакция от {UPDATED}</span>
          <span><Ic name="building" size={15} />Применимое право: Республика Казахстан</span>
        </div>
      </Rv>

      <div className="fed-legal-layout">
        <aside className="fed-legal-toc">
          <b>Содержание</b>
          {sections.map((section, index) => (
            <a key={section.title} href={`#legal-${index + 1}`}>{index + 1}. {section.title}</a>
          ))}
        </aside>

        <main className="fed-legal-content">
          {sections.map((section, index) => (
            <Rv as="section" key={section.title} id={`legal-${index + 1}`}>
              <span className="fed-legal-number">{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h2>{section.title}</h2>
                {section.body.map((block, blockIndex) => {
                  if (Array.isArray(block)) return (
                    <ul key={blockIndex}>{block.map((item) => <li key={item}>{item}</li>)}</ul>
                  );
                  return <p key={blockIndex}>{block}</p>;
                })}
              </div>
            </Rv>
          ))}

          <div className="fed-legal-contact">
            <Ic name="phone" size={22} />
            <div>
              <h2>Контакты для юридически значимых обращений</h2>
              <p>{OPERATOR}, Республика Казахстан, Северо-Казахстанская область, г. Петропавловск.</p>
              <p>Телефон: <a href="tel:+77006780221">+7 700 678 0221</a> · Telegram: <a href="https://t.me/fc_sko" target="_blank" rel="noreferrer">@fc_sko</a> · <button onClick={() => go('contacts')}>форма обратной связи</button>.</p>
              <p>В обращении укажите Ф.И.О. или идентификатор аккаунта, суть требования и сведения, позволяющие подтвердить принадлежность аккаунта. Мы вправе запросить разумное подтверждение личности, не собирая избыточные данные.</p>
            </div>
          </div>

          <div className="fed-legal-links">
            <span>Официальные источники:</span>
            <a href="https://adilet.zan.kz/rus/docs/Z1300000094" target="_blank" rel="noreferrer">Закон РК «О персональных данных и их защите»</a>
            <a href="https://adilet.zan.kz/rus/docs/Z1500000418" target="_blank" rel="noreferrer">Закон РК «Об информатизации»</a>
            <a href="https://adilet.zan.kz/rus/docs/K2600000255" target="_blank" rel="noreferrer">Цифровой кодекс РК</a>
          </div>
        </main>
      </div>
    </div>
  );
}

const privacySections = [
  {
    title: 'Общие положения и оператор',
    body: [
      `Настоящая Политика определяет порядок сбора, обработки и защиты персональных данных на сайте cybersportleague.kz и в связанных сервисах Федерации киберспорта Северо-Казахстанской области. Собственником и (или) оператором базы персональных данных является ${OPERATOR}.`,
      'Политика подготовлена в соответствии с Конституцией Республики Казахстан, Законом РК «О персональных данных и их защите», Законом РК «Об информатизации», Цифровым кодексом РК и иными применимыми нормативными правовыми актами.',
      'Используя сайт, создавая аккаунт, подавая заявку либо отдельно отмечая согласие, пользователь подтверждает, что ознакомился с Политикой. Если для конкретной операции законодательство требует отдельного согласия, такое согласие запрашивается до начала операции.',
    ],
  },
  {
    title: 'Какие данные мы обрабатываем',
    body: [[
      'Данные аккаунта: адрес электронной почты, никнейм, Ф.И.О. (если указано), номер телефона, пароль в виде криптографического хеша, язык интерфейса, статус подтверждения и тип аккаунта.',
      'Данные внешней авторизации: провайдер (например, Google), идентификатор провайдера, подтверждённый e-mail и технические токены, необходимые для поддержания авторизации.',
      'Турнирные данные: выбранная дисциплина и команда, заявки, статусы допуска, игровой ID, игровой никнейм, Telegram, состав команды, роль капитана, код приглашения, результаты, готовность к матчу и загруженные подтверждения результата.',
      'Обращения и коммуникации: имя, телефон, e-mail, содержание обращения, заявки на партнёрство, история уведомлений и ответы организаторов.',
      'Технические данные: IP-адрес, дата и время запросов, тип браузера и устройства, сведения об ошибках, cookie/localStorage, access- и refresh-токены, push-подписка, идентификатор устройства и данные, необходимые для защиты от злоупотреблений.',
      'Общедоступные данные: название и тег команды, игровые никнеймы, турнирная сетка, результаты и иная информация, которую пользователь согласился опубликовать для проведения соревнования.',
    ]],
  },
  {
    title: 'Цели и правовые основания',
    body: [
      'Мы обрабатываем данные в объёме, необходимом и достаточном для регистрации и аутентификации, ведения профиля, формирования команд, приёма и модерации заявок, проведения турниров и чек-ина, публикации сетки и результатов, выдачи призов, связи с участниками, технической поддержки, безопасности и исполнения требований законодательства.',
      'Основаниями обработки являются согласие субъекта, заключение и исполнение пользовательского соглашения и правил конкретного турнира, выполнение установленных законом обязанностей, а в предусмотренных законом случаях — защита законных интересов оператора и пользователей.',
      'Данные не используются для принятия решений, порождающих юридические последствия исключительно на основе автоматизированной обработки. Турнирный допуск, модерация и спорные результаты могут проверяться человеком.',
    ],
  },
  {
    title: 'Сбор, хранение и сроки',
    body: [
      'Основная база аккаунтов и турнирных заявок размещается и хранится на территории Республики Казахстан. Данные собираются непосредственно от пользователя, его капитана команды, провайдера авторизации либо автоматически при использовании сайта.',
      'Данные хранятся не дольше, чем необходимо для заявленных целей: аккаунт — пока он действует; турнирные заявки и результаты — в течение срока проведения соревнования и разумного архивного срока; обращения и журналы безопасности — на срок рассмотрения, предотвращения нарушений и защиты прав сторон. Сведения, подлежащие хранению по закону или необходимые для разрешения спора, сохраняются на установленный законом срок.',
      'После достижения целей, прекращения отношений, истечения срока хранения или законного отзыва согласия данные уничтожаются либо обезличиваются, если их дальнейшее хранение не требуется законодательством Республики Казахстан.',
    ],
  },
  {
    title: 'Передача третьим лицам и за рубеж',
    body: [
      'Доступ получают только работники и организаторы, которым он необходим для соответствующей задачи. Мы можем привлекать поставщиков хостинга, e-mail, Google OAuth, Telegram, Firebase Cloud Messaging и Web Push, технической аналитики и защиты сайта. Они получают только необходимый объём данных и обязаны обеспечивать конфиденциальность и безопасность.',
      'Трансграничная передача возможна при использовании международных сервисов только при наличии предусмотренного законом основания и надлежащего уровня защиты либо отдельного согласия субъекта. Мы не продаём персональные данные и не передаём их для чужой самостоятельной рекламы.',
      'Данные могут быть раскрыты государственным органам, судам и правоохранительным органам в случаях и порядке, прямо установленных законодательством Республики Казахстан.',
    ],
  },
  {
    title: 'Cookie, локальное хранилище и уведомления',
    body: [
      'Сайт использует технически необходимые cookie и localStorage для сохранения языка, сессии, маршрута, состояния интерфейса и локального кэша. Без них авторизация и часть функций могут работать некорректно.',
      'Push-уведомления включаются только после разрешения браузера. Разрешение можно отозвать в настройках устройства или браузера. Пользователь может очистить локальные данные в настройках браузера; это приведёт к выходу из аккаунта и сбросу локальных настроек.',
    ],
  },
  {
    title: 'Права пользователя',
    body: [[
      'Узнать о наличии и обработке своих данных, целях, источниках, способах и сроках хранения.',
      'Получить перечень обрабатываемых данных и потребовать их изменения или дополнения при наличии оснований.',
      'Потребовать блокирования или уничтожения недостоверных, незаконно полученных либо обрабатываемых с нарушением данных.',
      'Отозвать согласие на сбор, обработку, распространение, передачу третьим лицам и трансграничную передачу, кроме предусмотренных законом исключений и неисполненных обязательств.',
      'Обжаловать действия оператора в уполномоченный орган или суд и требовать защиты законных интересов.',
    ]],
  },
  {
    title: 'Порядок отзыва согласия и удаления аккаунта',
    body: [
      'Запрос направляется через контакты внизу страницы. После подтверждения личности мы прекращаем обработку в течение пятнадцати рабочих дней, если дальнейшая обработка не требуется законом либо для исполнения неисполнённого обязательства; в противном случае предоставляем мотивированный ответ.',
      'Удаление аккаунта может повлечь невозможность участия в турнирах. Уже опубликованные результаты могут быть сохранены в обезличенном или минимально необходимом виде для целостности спортивной истории и разрешения споров.',
    ],
  },
  {
    title: 'Безопасность и нарушения',
    body: [
      'Мы применяем организационные и технические меры: разграничение доступа, хеширование паролей, защищённое соединение, журналирование значимых действий, резервное копирование, ограничение частоты запросов и контроль доступа к загруженным файлам.',
      'Ни один способ передачи данных не гарантирует абсолютную безопасность. При выявлении нарушения безопасности мы принимаем меры по локализации, устранению последствий и уведомлению уполномоченного органа и субъектов в случаях, предусмотренных законом.',
    ],
  },
  {
    title: 'Данные несовершеннолетних',
    body: [
      'Если участник не достиг возраста, позволяющего самостоятельно дать юридически действительное согласие или принять условия турнира, обработка осуществляется с согласия его законного представителя. Организатор вправе запросить подтверждение возраста и согласия представителя в минимально необходимом объёме.',
    ],
  },
  {
    title: 'Изменение Политики',
    body: [
      'Мы вправе обновлять Политику при изменении законодательства или функций сайта. Новая редакция публикуется на этой странице с датой обновления. Существенные изменения, требующие нового согласия, применяются после его получения.',
    ],
  },
];

const termsSections = [
  {
    title: 'Статус и принятие условий',
    body: [
      `Настоящее Пользовательское соглашение регулирует использование сайта cybersportleague.kz и сервисов проекта. Администратором сайта является ${OPERATOR}.`,
      'Регистрация аккаунта, вход, подача заявки, создание команды либо продолжение использования сайта после ознакомления с условиями означает принятие Соглашения. Правила конкретного турнира являются его неотъемлемой частью; при расхождении специальные правила турнира применяются к соответствующему соревнованию.',
    ],
  },
  {
    title: 'Аккаунт и безопасность',
    body: [[
      'Пользователь предоставляет достоверные и актуальные сведения и своевременно обновляет их.',
      'Один аккаунт используется только его владельцем. Передача пароля, токена сессии или аккаунта другому лицу запрещена.',
      'Пользователь отвечает за действия через свой аккаунт до уведомления администрации о компрометации.',
      'Никнейм не должен нарушать закон, права третьих лиц, содержать оскорбления, дискриминацию, пропаганду насилия или выдавать пользователя за другое лицо.',
      'Администрация вправе запросить подтверждение e-mail, телефона, возраста, игрового ID или принадлежности аккаунта в объёме, необходимом для безопасности и допуска к турниру.',
    ]],
  },
  {
    title: 'Команды и турнирные заявки',
    body: [
      'Капитан создаёт команду, приглашает участников, подаёт и отзывает заявку в пределах доступной функциональности и подтверждает, что участники согласны на включение в состав. Каждый участник отвечает за достоверность собственного профиля.',
      'Подача заявки не гарантирует допуск. Организатор проверяет соответствие регламенту, состав, возрастные и территориальные ограничения, игровые данные и иные условия. После начала матчей изменение состава или отзыв заявки могут быть ограничены для сохранения целостности сетки.',
      'Коды приглашения являются конфиденциальными. Действия лица, вступившего по переданному коду, считаются совершёнными с разрешения капитана, если администрация не была своевременно уведомлена о компрометации.',
    ],
  },
  {
    title: 'Правила поведения и честная игра',
    body: [[
      'Соблюдать законодательство РК, настоящий документ, регламент турнира и законные решения организаторов и судей.',
      'Не использовать читы, уязвимости, подмену личности, договорные матчи, мультиаккаунты, вмешательство в работу сайта или игры.',
      'Не размещать вредоносный код, спам, незаконный, оскорбительный, дискриминационный материал и персональные данные третьих лиц без основания.',
      'Не препятствовать другим пользователям, не угрожать участникам и организаторам и не обходить блокировки.',
      'Сохранять доказательства результата и по запросу предоставлять корректные скриншоты, записи или игровые данные.',
    ]],
  },
  {
    title: 'Модерация и санкции',
    body: [
      'При нарушении администрация вправе запросить объяснения, отклонить заявку, скрыть материал, ограничить функцию, аннулировать результат, применить техническое поражение, временно заблокировать или удалить аккаунт. Мера должна быть соразмерной нарушению и регламенту.',
      'В срочных случаях, связанных с безопасностью, мошенничеством или устойчивостью сервиса, ограничение может применяться до завершения проверки. Пользователь вправе обжаловать решение через контакты, приложив факты и доказательства.',
    ],
  },
  {
    title: 'Контент и интеллектуальная собственность',
    body: [
      'Права на дизайн, программный код, тексты, логотипы и материалы сайта принадлежат их правообладателям. Использование сайта не передаёт пользователю исключительных прав.',
      'Загружая никнейм, логотип команды, изображение или подтверждение результата, пользователь гарантирует наличие необходимых прав и предоставляет администрации безвозмездное неисключительное право использовать материал в объёме, необходимом для проведения, освещения и архивирования турнира. Пользователь может отозвать разрешение на будущую публикацию, если сохранение не требуется законом или законными целями спортивного архива.',
    ],
  },
  {
    title: 'Призы и решения турнира',
    body: [
      'Размер, форма, порядок и сроки выдачи призов определяются регламентом и анонсом конкретного турнира. Налоги и обязательные платежи исполняются сторонами согласно законодательству Республики Казахстан.',
      'Результаты могут быть скорректированы при технической ошибке, подтверждённом нарушении или решении судейской коллегии. Организатор обязан рассматривать спор добросовестно и на основании доступных доказательств.',
    ],
  },
  {
    title: 'Доступность и сторонние сервисы',
    body: [
      'Сайт предоставляется в доступном состоянии без гарантии непрерывной и безошибочной работы. Возможны профилактика, обновления, сбои связи, игровых платформ и внешних сервисов. Мы принимаем разумные меры для восстановления и сохранности данных.',
      'Ссылки на Google, Telegram, YouTube, TikTok, игровые платформы и иные сайты ведут к независимым сервисам с собственными условиями. Администрация не отвечает за их содержание и действия вне пределов установленной законом ответственности.',
    ],
  },
  {
    title: 'Ответственность',
    body: [
      'Каждая сторона отвечает за нарушение обязательств по законодательству Республики Казахстан. Пользователь возмещает документально подтверждённый вред, причинённый его противоправными действиями, в установленном законом порядке.',
      'Ограничения ответственности не применяются, если это запрещено законом, включая случаи умысла, грубой неосторожности, нарушения прав потребителя или законодательства о персональных данных.',
    ],
  },
  {
    title: 'Прекращение использования',
    body: [
      'Пользователь может прекратить использование и запросить удаление аккаунта. Администрация может прекратить доступ при существенном нарушении, по требованию закона либо при закрытии сервиса, предварительно уведомив пользователя, когда это разумно и возможно.',
      'Положения об интеллектуальной собственности, ответственности, спорах и хранении необходимых архивных данных продолжают действовать после прекращения отношений в применимой части.',
    ],
  },
  {
    title: 'Применимое право и споры',
    body: [
      'Соглашение регулируется законодательством Республики Казахстан. Стороны стремятся урегулировать спор путём письменной претензии. Срок ответа — пятнадцать рабочих дней, если иной срок не установлен законом или регламентом.',
      'При недостижении соглашения спор передаётся в суд по правилам подсудности, установленным законодательством Республики Казахстан. Настоящее условие не ограничивает императивные права потребителей и субъектов персональных данных.',
    ],
  },
  {
    title: 'Изменение условий',
    body: [
      'Новая редакция публикуется на сайте с датой вступления в силу. Изменения не имеют обратной силы, если иное не требуется законом. Если изменение существенно влияет на права пользователя, администрация размещает заметное уведомление и при необходимости запрашивает повторное согласие.',
    ],
  },
];

export function PrivacyPolicy() {
  return <LegalLayout type="Защита данных" title="Политика конфиденциальности" intro="Правила обработки и защиты персональных данных пользователей cybersportleague.kz." sections={privacySections} />;
}

export function UserAgreement() {
  return <LegalLayout type="Правила платформы" title="Пользовательское соглашение" intro="Условия использования сайта, аккаунтов, команд и турнирных сервисов Федерации киберспорта СКО." sections={termsSections} />;
}
